/**
 * Money is integer pence throughout the system — every layer, every wire
 * format. Pounds exist only in this file: the formatter and the input parser.
 *
 * One implementation, imported by the Worker and the client alike, because two
 * money formatters is two rounding conventions waiting to disagree.
 */

/** U+2212. A hyphen is not a minus sign and does not align in tabular figures. */
export const MINUS = "−";

const CURRENCY = "£";

/** Math.round breaks ties towards +Infinity, which biases debits against credits. */
function roundHalfAwayFromZero(n: number): number {
  return n < 0 ? -Math.round(-n) : Math.round(n);
}

export function penceToPounds(pence: number): number {
  return pence / 100;
}

/**
 * Only for numbers that were pence to begin with. A decimal typed by a person
 * or read from a file goes through `parseAmountToPence`, which works on the
 * digits: by the time "1.005" is a double it is already 1.00499… and the penny
 * is gone.
 */
export function poundsToPence(pounds: number): number {
  return roundHalfAwayFromZero(pounds * 100);
}

/**
 * Parse a statement amount into pence. Handles currency symbols, thousands
 * separators, both minus characters, and the parenthesised negatives some UK
 * exports use for debits. Returns null when the cell is empty or not a number.
 *
 * Display never uses brackets for negatives; imports have to accept them
 * because real bank exports emit them.
 */
export function parseAmountToPence(raw: string | undefined | null): number | null {
  if (raw == null) return null;
  let text = raw.trim();
  if (text === "") return null;

  let negative = false;
  if (text.startsWith("(") && text.endsWith(")")) {
    negative = true;
    text = text.slice(1, -1);
  }

  text = text.replace(/[£$€,\s]/g, "").replace(/−/g, "-");
  if (text === "" || text === "-" || text === "+") return null;

  const match = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(text);
  if (!match) return null;

  const [, sign = "", whole = "", fraction = ""] = match;
  if (whole === "" && fraction === "") return null;

  // Integer arithmetic on the digits, never `Number(text) * 100`. The double
  // nearest to "1.005" is 1.00499…, so the float route silently loses the
  // penny — which is the exact failure the integer-pence rule exists to stop.
  const units = whole === "" ? 0 : Number(whole);
  if (!Number.isSafeInteger(units)) return null;

  const hundredths = Number((fraction.slice(0, 2) + "00").slice(0, 2));
  // Anything past two decimals decides the rounding, half away from zero.
  const remainder = fraction.slice(2);
  const roundUp = remainder !== "" && remainder.charCodeAt(0) >= "5".charCodeAt(0);

  const magnitude = units * 100 + hundredths + (roundUp ? 1 : 0);
  if (!Number.isSafeInteger(magnitude)) return null;

  return negative || sign === "-" ? -magnitude : magnitude;
}

/**
 * What a person actually types, into pence. The only place a string becomes
 * money on the client, which is exactly how many places that should be.
 */
export function parseMoneyInput(raw: string): number | null {
  return parseAmountToPence(raw);
}

/** Digits only, grouped, fixed precision. No sign, no symbol. */
function formatMagnitude(pence: number, decimals: 0 | 2): string {
  return penceToPounds(Math.abs(pence)).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export interface FormatMoneyOptions {
  /** Show a leading + on positive amounts. Off by default. */
  signed?: boolean;
  /** Drop the pence. Summary tiles only — never a table, never an editable field. */
  decimals?: 0 | 2;
}

/**
 * The canonical rendering: `£1,247.30`, `−£24.50`.
 *
 * Always a currency symbol, always thousands separators, always two decimals
 * unless explicitly told otherwise, and a real minus sign in front of the
 * symbol rather than inside it.
 */
export function formatMoney(pence: number, options: FormatMoneyOptions = {}): string {
  const { signed = false, decimals = 2 } = options;
  const magnitude = `${CURRENCY}${formatMagnitude(pence, decimals)}`;
  if (pence < 0) return `${MINUS}${magnitude}`;
  return signed && pence > 0 ? `+${magnitude}` : magnitude;
}

/**
 * `£12.4k`, `£1.2m`. For summary tiles where the exact figure is one tap away.
 *
 * Never use this on anything a person might read back to someone, type into a
 * form, or reconcile against a bank statement.
 */
export function formatMoneyCompact(pence: number): string {
  const abs = Math.abs(pence);
  const sign = pence < 0 ? MINUS : "";

  if (abs < 100_000) return formatMoney(pence);

  const [divisor, suffix] = abs >= 100_000_000 ? [100_000_000, "m"] : [100_000, "k"];
  const scaled = abs / divisor;
  // 12.4k, but 124k rather than 124.0k — three significant figures is plenty.
  const digits = scaled >= 100 ? 0 : 1;
  return `${sign}${CURRENCY}${scaled.toFixed(digits)}${suffix}`;
}

/**
 * The sign convention is the *account's*: spending on a credit card and
 * spending from a current account both read as money out, so direction is
 * stored explicitly rather than inferred from the sign of a balance.
 */
export function directionOf(pence: number): "in" | "out" {
  return pence < 0 ? "out" : "in";
}
